// =============================================================================
// DatePickerInput - Input data con MiniCalendar dropdown
// =============================================================================

import { useState, useCallback } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { useDateLocale } from '../../hooks/useDateLocale';
import { MiniCalendar } from './MiniCalendar';
import { cn } from '../../lib/utils';

interface DatePickerInputProps {
  /** Valore in formato 'yyyy-MM-dd' o ISO string */
  value: string;
  onChange: (value: string) => void;
  /** Formato di visualizzazione (default: 'd MMM yyyy') */
  displayFormat?: string;
  placeholder?: string;
  className?: string;
  /** Allineamento dropdown */
  align?: 'left' | 'right';
  /** Dimensione: 'sm' | 'md' (default) */
  size?: 'sm' | 'md';
}

export function DatePickerInput({
  value,
  onChange,
  displayFormat = 'd MMM yyyy',
  placeholder = '—',
  className,
  align = 'left',
  size = 'md',
}: DatePickerInputProps) {
  const [open, setOpen] = useState(false);
  const dateLocale = useDateLocale();

  const parsed = value ? parseISO(value.split('T')[0]) : null;
  const isValidDate = parsed && isValid(parsed);

  const handleSelect = useCallback((date: Date) => {
    onChange(format(date, 'yyyy-MM-dd'));
  }, [onChange]);

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center gap-2 border border-gray-300 rounded-lg bg-white text-left transition-colors',
          'hover:border-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none',
          size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-3 py-2 text-sm',
        )}
      >
        <CalendarDays className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className={isValidDate ? 'text-gray-900' : 'text-gray-400'}>
          {isValidDate ? format(parsed, displayFormat, { locale: dateLocale }) : placeholder}
        </span>
      </button>
      {open && (
        <MiniCalendar
          selectedDate={isValidDate ? parsed : new Date()}
          onSelect={handleSelect}
          onClose={() => setOpen(false)}
          align={align}
        />
      )}
    </div>
  );
}
